import React, { useEffect, useRef, useState } from "react";
import { InputAdornment, Button, Grid, TextField, MenuItem, Checkbox, FormControlLabel, FormGroup } from '@mui/material'
//inputadornment is a new import
import { DatePicker } from '@mui/lab';
import GridBreak from '../../../components/GridBreak'
import IFramePopUp from '../../../components/Pop_ups/IFramePopUp';
import DragFiles from "../../../components/FileManagement/DragFiles";
import { fileTo64Arr, handleFormNumbers } from "../../../util/generalHelp";
import { createOfferDraft, removeOfferDraft, submitDraft, getFieldClause } from "../../../util/offerLettersHelp"; //getfieldclause should theoretically work w new stuff in configs, but we'll see
import { previewDocusign } from "../../../util/docusignHelper";
import { useBeforeunload } from 'react-beforeunload';
import { useBackListener } from "../../../util/Hooks";
import moment from "moment";
import { getProfileFromEmail } from "../../../util/ProfileHelper";

const OPTIONAL_FIELDS = ["address"]

// page 1 paragraphs of the Canadian offer letter; required, so always shown and editable
const BODY_SECTIONS = [
    { key: 'start_of_employment', label: 'Start Of Employment' },
    { key: 'compensation', label: 'Compensation' },
    { key: 'time_off', label: 'Time Off' },
    { key: 'contingency', label: 'Contingency' }
]

function OfferLetterCAN(props){

    const defaultObj = {
        document_args: {
            "position": "",
            salary: 0,
            vacation_days: 10,
            sick_days: 10,
            pay_type: "SALARY",
            city: "",
            province: "",
            mailing_code: "",
            start_date: moment().format('YYYY-MM-DD'),
            due_date: moment().format('YYYY-MM-DD'),
            prob_end_date: moment().format('YYYY-MM-DD'),
            authority_signer: ""
        },
        benefits: true,
        region: 'CAN',
        first_name: "",
        last_name: "",
        email_address: "",
        signatory: "",
        authority_id: null,
        "return_url": window.location.origin.toString()
    }

    // blank form used after an offer is sent; region/return_url/benefits are not user inputs so they are kept.
    // day counts are 0 because their inputs are hidden until toggled on and the backend requires them
    const emptyObj = () => ({
        document_args: {
            position: null,
            salary: null,
            vacation_days: 0,
            sick_days: 0,
            pay_type: null,
            address: null,
            city: null,
            province: null,
            mailing_code: null,
            start_date: null,
            due_date: null,
            prob_end_date: null,
            authority_signer: null
        },
        benefits: true,
        region: 'CAN',
        first_name: null,
        last_name: null,
        email_address: null,
        signatory: null,
        authority_id: null,
        recruiter: null,
        account_manager: null,
        client_id: null,
        contact_id: null,
        "return_url": window.location.origin.toString()
    })

    // state to start the form with when a previous offer letter is loaded (see ManageOffers); null for a blank form
    const buildLoadedForm = () => {
        if (!props.loadedOffer) return null
        const { offer, form } = props.loadedOffer
        if (form) {
            const { body_sections, ...savedObj } = form.request_obj
            return {
                requestObj: { ...defaultObj, ...savedObj, return_url: defaultObj.return_url,
                    document_args: { ...defaultObj.document_args, ...savedObj.document_args } },
                bodySections: body_sections || {}
            }
        }
        // offer letters drafted before the whole form was saved only have what is stored on the envelope
        const [firstName, ...lastName] = offer.name.split(' ')
        const authority = props.authorities.find((auth) => auth.id === offer.authority_id)
        return {
            requestObj: { ...defaultObj, first_name: firstName, last_name: lastName.join(' '), email_address: offer.email_address,
                authority_id: offer.authority_id, contact_id: offer.contact_id ?? '',
                document_args: { ...defaultObj.document_args, authority_signer: authority?.name || '' } },
            bodySections: {}
        }
    }
    const [loaded] = useState(buildLoadedForm)

    const [requestObj, setRequestObj] = useState(loaded ? loaded.requestObj : { ...defaultObj })
    const [formKey, setFormKey] = useState(0)
    const [previewUrl, setPreviewUrl] = useState()
    const [previewClicked, setPreviewClicked] = useState(false)
    const [envelopeId, setEnvelopeId] = useState("")
    const [fileArr, setFileArr] = useState([])
    const [uploadStatus, setUploadStatus] = useState('error')
    const [defaultOfferText] = useState({})
    const [showAddress, setShowAddress] = useState(Boolean(requestObj.document_args.address));
    const [showVacationDays, setShowVacationDays] = useState(Number(requestObj.document_args.vacation_days) > 0);
    const [showSickDays, setShowSickDays] = useState(Number(requestObj.document_args.sick_days) > 0);
    // text for the page 1 body sections, keyed by section, and the default wording they start from
    const [bodySections, setBodySections] = useState(loaded ? loaded.bodySections : {})
    const [bodyDefaults, setBodyDefaults] = useState(null)

    useEffect(() => {
        const fetchSection = (key) => new Promise((resolve) =>
            getFieldClause(key, "OfferLetter", 'can_offer_document', (json) => resolve(json && json.clauses ? json.clauses[0] : null)))
        Promise.all(BODY_SECTIONS.map(({ key }) => fetchSection(key))).then((texts) => {
            let defaults = {}
            BODY_SECTIONS.forEach(({ key }, i) => { if (texts[i] !== null) defaults[key] = texts[i] })
            if (texts.includes(null)) {
                props.setError("The default letter section text could not be fetched. This is usually due to server error.")
            }
            setBodyDefaults(defaults)
            // sections from a loaded offer letter keep their saved text
            setBodySections(prev => ({ ...defaults, ...prev }))
        })
    // load the defaults once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // void the open draft if the form goes away with one (e.g. another offer letter is loaded or the country is switched)
    const draftId = useRef()
    draftId.current = requestObj.id
    useEffect(() => () => {
        if (draftId.current){
            removeOfferDraft(draftId.current, (resp) => { }, true)
        }
    }, [])
    const [errorMessage, setErrorMessage] = useState("There was an error! Please try again.")

    useEffect(() => {
        if (fileArr && fileArr.length > 0){
            setUploadStatus('success')
        }
        else {
            setUploadStatus('pending')
        }
    }, [fileArr])

    useBackListener(({location}) => {
        if (requestObj.id){
            removeOfferDraft(requestObj.id, (resp)=>{ }, true) //comes from offerlettershelp
        }
    })

    useBeforeunload((event) => {
        if (requestObj.id){
            removeOfferDraft(requestObj.id, (resp) => { }, true) //comes from offerlettershelp
        }
    });

    const handleAddressToggle = (e) => {
        const isChecked = e.target.checked;
        setShowAddress(isChecked);

        if (!isChecked) {
            updateDocument('address', '');
        }
    };

    // unchecking a day count sets it to 0, which leaves it out of the Time Off sentence
    const handleVacationDaysToggle = (e) => {
        const isChecked = e.target.checked;
        setShowVacationDays(isChecked);

        if (!isChecked) {
            updateDocument('vacation_days', 0);
        }
    };

    const handleSickDaysToggle = (e) => {
        const isChecked = e.target.checked;
        setShowSickDays(isChecked);

        if (!isChecked) {
            updateDocument('sick_days', 0);
        }
    };

    const closePreview = () => {
        setPreviewClicked(false)
        previewDocusign(envelopeId, (json) => {
            if (json) {
                setPreviewUrl(json.redirect_url)
            }
        })
    }

    const handleAuthorityChange = (e) => {
        const selectedId = e.target.value;
        const selectedAuthority = props.authorities.find((auth) => auth.id === selectedId);

        setRequestObj(prev => ({
            ...prev,
            authority_id: selectedId,
            document_args: {
                ...prev.document_args,
                authority_signer: selectedAuthority?.name || ''
            }
        }));
    };

    const updateDocument = (key, value) => {
        let newDoc = {...requestObj}
        if (key == 'seq_hire_helper') {
                newDoc['document_args']['seq_hire_statement'] = defaultObj.document_args.seq_hire_statement.replace(/X/, `${value}`)
            }
        newDoc['document_args'][key] = value
        setRequestObj(newDoc)
    }

    const handleDraft = async () => {
        if (previewUrl && requestObj.id){
            props.setLoading(true)
            submitDraft(requestObj.id, envelopeId, (resp) => {
                if (resp){
                    setRequestObj(emptyObj())
                    setFileArr([])
                    setPreviewUrl()
                    setEnvelopeId("")
                    setShowAddress(false)
                    setShowVacationDays(false)
                    setShowSickDays(false)
                    setBodySections(bodyDefaults ? { ...bodyDefaults } : {})
                    setFormKey(formKey + 1) // remounts DragFiles so its internal file list is cleared
                    if (props.onComplete){
                        props.onComplete()
                    }
                } else {
                    props.onError({})
                }
                props.setLoading(false)
        })
        
        } else {
            props.setLoading(true)
            setPreviewUrl()
            if (checkJson(requestObj)){ //was formerly requestObj.document_args
                getProfileFromEmail(async (profile) =>
                {
                let request_obj = {...requestObj}
                // the street name is optional, and the backend rejects empty document_args, so leave it out when blank
                request_obj.document_args = {...requestObj.document_args}
                if (!request_obj.document_args.address) {
                    delete request_obj.document_args.address
                }
                request_obj.document_b64s = await handleFile(fileArr)
                request_obj.body_sections = bodySections
                const jsonSend = {request_obj: request_obj, optional_fields: OPTIONAL_FIELDS, contract: 'OfferLetter', fields: ['fieldsOfferLetter'], fieldsOfferLetter: {}, default_changes: defaultOfferText, sender: profile.id}
                createOfferDraft(jsonSend, (resp) => {
                    if (resp) {
                        updateOffer('id', resp.id)
                        setPreviewUrl(resp.redirect_url)
                        setUploadStatus('disabled')
                        setEnvelopeId(resp.envelope_id)
                    } else {
                        props.onError({})
                    }
                    props.setLoading(false)
                })
            })
            } else {
                props.onError({ errorTitle: 'Missing Form Fields', errorContent: errorMessage })
                props.setLoading(false)
            }
        }
    }

    const checkJson = (json) => {
        for (const key of Object.keys(json)){
            if (json[key] === null || json[key] === undefined || json[key] === ''){
                if (!props.optionalFields.includes(key))
                {
                    setErrorMessage("Required field not found: " + String(key))
                    return false
                }
            }

            if (json[key] != null && json[key].constructor == Object){
                if (!checkJson(json[key]))
                {
                    setErrorMessage("Required field not found: " + String(key))
                    return false
                }
                continue
            }
        }
        return true
    }

    const updateOffer = (key, value, del = false) => {
        let newOffer = {...requestObj}
        if (!del){
            newOffer[key] = value
            setRequestObj(newOffer)
        }
        else {
            delete newOffer[key]
            setRequestObj(newOffer)
        }
    }

    const handleClear = () => {
        if (requestObj.id){
            props.setLoading(true)
            removeOfferDraft(requestObj.id, (resp) => {
                if (resp) {
                    let newOffer = {...requestObj}
                    delete newOffer['id']
                    setRequestObj(newOffer)
                    setPreviewUrl()
                    setUploadStatus('pending')
                }
                else {
                    props.onError({})
                }
                props.setLoading(false)
            })
        }
    }

    const handleFile = async (fileArr) => {
        return await fileTo64Arr(fileArr)
    }

    const handleNumberForm = (e, key, document = false, decimals = 0, maxdigits = undefined) => {
        let modfiedNum = handleFormNumbers(e.target.value, decimals, false, maxdigits)
        if (modfiedNum !== undefined) {
            if (document) {
                updateDocument(key, modfiedNum)
            } else {
                updateOffer(key, modfiedNum)
            }
        }
    }

    return (
    <>
        <Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', width: '100%', padding: '1%' }}>
        <IFramePopUp open={previewClicked} url={previewUrl} onClose={closePreview} maxRedirect={2} />
            <FormGroup item sx={{ width: '100%', padding: '1%'}}>
                {BODY_SECTIONS.map(({ key, label }) => (
                    <React.Fragment key={key}>
                        {/* required section: always selected and can't be unchecked, but its text stays editable */}
                        <FormControlLabel control={<Checkbox checked disabled />} label={`Edit ${label}`} />
                        {key in bodySections ?
                            <Grid sx = {{'margin-left': '10%', 'width': '75%'}}>
                                <TextField sx = {{'margin': '1%'}} label={`Current ${label} Text`} disabled={Boolean(previewUrl)}
                                    helperText="Words in {braces} such as {position} are filled in automatically"
                                    value={bodySections[key] ?? ''}
                                    onChange={(e) => setBodySections({...bodySections, [key]: e.target.value})}
                                    fullWidth required multiline />
                            </Grid> : <></>
                        }
                    </React.Fragment>
                ))}
            </FormGroup>

            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="First Name" onChange={(e) => { e.preventDefault(); updateOffer('first_name', e.target.value) }}
                    value={requestObj.first_name ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="Last Name" onChange={(e) => { e.preventDefault(); updateOffer('last_name', e.target.value) }}
                    value={requestObj.last_name ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="Email Address" onChange={(e) => { e.preventDefault(); updateOffer('email_address', e.target.value) }}
                    value={requestObj.email_address ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showAddress}
                            onChange={handleAddressToggle}
                            disabled={Boolean(previewUrl)}
                        />
                    }
                    label="Include Street Name"
                />
            </Grid>
            {showAddress && (
                <Grid item xs={3}>
                    <TextField
                        disabled={Boolean(previewUrl)}
                        label="Street Name"
                        onChange={(e) => { e.preventDefault(); updateDocument('address', e.target.value) }}
                        value={requestObj.document_args.address ?? ''}
                        fullWidth
                    />
                </Grid>
            )}
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="City" onChange={(e) => { e.preventDefault(); updateDocument('city', e.target.value) }}
                    value={requestObj.document_args.city ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="Province" onChange={(e) => { e.preventDefault(); updateDocument('province', e.target.value) }}
                    value={requestObj.document_args.province ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="Mailing Code" onChange={(e) => { e.preventDefault(); updateDocument('mailing_code', e.target.value) }}
                    value={requestObj.document_args.mailing_code ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} label="Position" onChange={(e) => { e.preventDefault(); updateDocument('position', e.target.value) }}
                    value={requestObj.document_args.position ?? ''} fullWidth required />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    disabled={Boolean(previewUrl)}
                    required
                    label="Pay Type"
                    onChange={(e) => { e.preventDefault(); updateDocument('pay_type', e.target.value) }}
                    value={requestObj.document_args.pay_type ?? ''}
                    fullWidth
                    select
                >
                    <MenuItem value="HOURLY">Hourly</MenuItem>
                    <MenuItem value="SALARY">Salary</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={3}>
                {requestObj.document_args.pay_type === 'SALARY' ?
                    <TextField disabled={Boolean(previewUrl)} label="Salary ($)" SelectProps='0.01' onChange={(e) => {
                        e.preventDefault();
                        handleNumberForm(e, 'salary', true, 2)
                    }}
                    InputProps={{
                            endAdornment:
                            <InputAdornment disableTypography position="end">CAD</InputAdornment>
                        }}
                        value={requestObj.document_args.salary ?? ''}  fullWidth required /> :
                    <TextField disabled={Boolean(previewUrl)} label="Salary ($ CAD/hr)" SelectProps='0.01' onChange={(e) => {
                        e.preventDefault();
                        handleNumberForm(e, 'salary', true, 2)
                    }}
                    InputProps={{
                            endAdornment:
                            <InputAdornment disableTypography position="end">CAD</InputAdornment>
                        }}
                        value={requestObj.document_args.salary ?? ''}  fullWidth required />
                }
            </Grid>
            <Grid item xs={3}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showVacationDays}
                            onChange={handleVacationDaysToggle}
                            disabled={Boolean(previewUrl)}
                        />
                    }
                    label="Include Vacation Days"
                />
            </Grid>
            {showVacationDays && (
                <Grid item xs={3}>
                    <TextField disabled={Boolean(previewUrl)} label="Vacation Days"
                    onChange = {(e) => {e.preventDefault(); handleNumberForm(e, 'vacation_days', true)}}
                    value={requestObj.document_args.vacation_days ?? ''} fullWidth required >
                    </TextField>
                </Grid>
            )}
            <Grid item xs={3}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showSickDays}
                            onChange={handleSickDaysToggle}
                            disabled={Boolean(previewUrl)}
                        />
                    }
                    label="Include Sick Days"
                />
            </Grid>
            {showSickDays && (
                <Grid item xs={3}>
                    <TextField disabled={Boolean(previewUrl)} label="Sick Days"
                    onChange = {(e) => {e.preventDefault(); handleNumberForm(e, 'sick_days', true)}}
                    value={requestObj.document_args.sick_days ?? ''} fullWidth required >
                    </TextField>
                </Grid>
            )}
            {/*backend complains if recruiters, account managers, client, signing authority, contacts not given, but seems unnecessary for current CAN offer draft, worth looking into */}
            {/*Authorities, ie. Approver */}
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} required label="Approver" onChange={handleAuthorityChange}
                    value={requestObj.authority_id ?? ''} fullWidth select>
                    {props.authorities.map((auth) => (
                        <MenuItem key={auth.id} value={auth.id}>
                            {auth.name}({auth.email_address})
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    disabled={Boolean(previewUrl)}
                    required
                    label="Recruiter"
                    onChange={(e) => updateOffer('recruiter', e.target.value)}
                    value={requestObj.recruiter || ''}
                    fullWidth
                    select
                >
                    {props.recruiters.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                            {r.first_name} {r.last_name} ({r.email})
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    disabled={Boolean(previewUrl)}
                    required
                    label="Account Manager"
                    onChange={(e) => updateOffer('account_manager', e.target.value)}
                    value={requestObj.account_manager || ''}
                    fullWidth
                    select
                >
                    {props.accountManagers.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                            {r.first_name} {r.last_name} ({r.email})
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    disabled={Boolean(previewUrl)}
                    required
                    label="Signatory"
                    onChange={(e) => updateOffer('signatory', e.target.value)}
                    value={requestObj.signatory || ''}
                    fullWidth
                    select
                >
                    {props.signatories.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                            {r.first_name} {r.last_name} ({r.email})
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <TextField label="Client" required={true} disabled={previewUrl} onChange={(e) => { e.preventDefault(); updateOffer('client_id', e.target.value) }}
                    value={requestObj.client_id ?? ''} fullWidth select>
                    {props.clients.map((auth) => (
                        <MenuItem key={auth.id} value={auth.id}>
                            {auth.short_name}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <TextField disabled={Boolean(previewUrl)} required label="Contacts" onChange={(e) => { e.preventDefault(); updateOffer('contact_id', e.target.value) }}
                    value={requestObj.contact_id ?? ''} fullWidth select>
                    {props.contacts.map((auth) => (
                        <MenuItem key={auth.id} value={auth.id}>
                            {auth.name}({auth.email_address})
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <DatePicker disabled={Boolean(previewUrl)} label="Start Date" value={requestObj.document_args.start_date}
                    onChange={(value) => updateDocument('start_date', value.format('YYYY-MM-DD').toString())}
                    renderInput={(params) => <TextField required fullWidth {...params} />}
                    minDate={requestObj.document_args.due_date ? moment.max(moment(), moment(requestObj.document_args.due_date)) : moment()} />
            </Grid>
            <Grid item xs={3}>
                <DatePicker disabled={Boolean(previewUrl)} label="Due Date" value={requestObj.document_args.due_date}
                    onChange={(value) => updateDocument('due_date', value.format('YYYY-MM-DD').toString())}
                    renderInput={(params) => <TextField required fullWidth {...params} />} minDate={moment.utc()}
                    maxDate={moment(requestObj.document_args.start_date ?? undefined)}/>
            </Grid>
            <Grid item xs={3}>
                <DatePicker disabled={Boolean(previewUrl)} label="Probation End Date" value={requestObj.document_args.prob_end_date}
                    onChange={(value) => updateDocument('prob_end_date', value.format('YYYY-MM-DD').toString())}
                    renderInput={(params) => <TextField required fullWidth {...params} />}
                    minDate={moment(requestObj.document_args.start_date ?? undefined)} />
            </Grid>
        <GridBreak />
            <Grid item container xs={12} sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <DragFiles key={formKey} types={props.fileTypes} multiple={true} disabled={Boolean(previewUrl)} onUpdate={setFileArr}
                    maxSize={15}
                    onTypeError={(error) => { setUploadStatus('error'); props.onError({ errorTitle: 'File Type Error', errorContent: error }) }}
                    onSizeError={(error) => { setUploadStatus('error'); props.onError({ errorTitle: 'File Size Error', errorContent: error }) }}
                    uploadStatus={uploadStatus} />
            </Grid>
            <Grid item xs={4} container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Button fullWidth disabled={!previewUrl || !requestObj.id} onClick={(e) => { e.preventDefault(); handleClear() }}>Cancel</Button>
            </Grid>
            <Grid item xs={4} container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Button fullWidth disabled={!previewUrl || !requestObj.id} onClick={(e) => { e.preventDefault(); setPreviewClicked(true) }}>Preview Document</Button>
            </Grid>
            <Grid item xs={4} container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Button fullWidth onClick={(e) => { e.preventDefault(); handleDraft() }}>{requestObj.id && previewUrl ? "Send" : "Create Draft"}</Button>
            </Grid>
        </Grid>
    </>);
}

export default OfferLetterCAN;